import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraFilterToolbar, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraIconButton, SpecraInput, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraPopover, SpecraSegmentedControl, SpecraTooltip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { MoreHorizontal, PanelRightOpen } from 'lucide-react';
import { useState } from 'react';
import { budgetBasisText, budgetText, overBudget } from './budgetText';
import { NewDraftPopover } from './NewDraftPopover';
/**
 * The Download menu items (WRT-13, OD-75): DOCX and PDF are always offered
 * (comments stripped); the preferred format goes first with "Requested
 * format"; "Download DOCX with comments" is for editors only.
 */ export function downloadItems(preferredFormat, canEdit, onDownload) {
    const docx = {
        id: 'docx',
        label: 'Download DOCX',
        meta: preferredFormat === 'docx' ? 'Requested format' : undefined,
        onClick: ()=>onDownload('docx', true)
    };
    const pdf = {
        id: 'pdf',
        label: 'Download PDF',
        meta: preferredFormat === 'pdf' ? 'Requested format' : undefined,
        onClick: ()=>onDownload('pdf', true)
    };
    const items = preferredFormat === 'pdf' ? [
        pdf,
        docx
    ] : [
        docx,
        pdf
    ];
    if (canEdit) {
        items.push({
            id: 'docx-comments',
            label: 'Download DOCX with comments',
            description: 'Internal copy; keeps reviewer comments',
            onClick: ()=>onDownload('docx', false)
        });
    }
    return items;
}
const DOWNLOAD_TESTIDS = {
    docx: Testids.officeWorkspace.downloadDocx,
    pdf: Testids.officeWorkspace.downloadPdf,
    'docx-comments': Testids.officeWorkspace.downloadDocxComments
};
function MenuItem({ children, onClick, testId }) {
    return /*#__PURE__*/ _jsx(SpecraButton, {
        className: "w-full justify-start",
        "data-testid": testId,
        onClick: onClick,
        size: "sm",
        variant: "ghost",
        children: children
    });
}
/**
 * The workspace action bar (WRT-04 §4): document + draft switch, "+ New draft",
 * the draft menu, save status, Compare, Download and More, and the caller's CTA.
 */ export function WorkspaceActionBar({ activeMain, alternates, budget, canEdit, canExport, checkingExactPages, cta, mainVersionNumber, mains, onArchiveDraft, onCheckExactPages, onCompare, onCreateDraft, onDownload, onPromoteDraft, onRenameDraft, onReplaceFromFile, onSelectDocument, onShowSide, preferredFormat, recentVersions, selectedDocumentId, status }) {
    const [downloadAnchor, setDownloadAnchor] = useState(null);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [draftMenuAnchor, setDraftMenuAnchor] = useState(null);
    const [draftMenuOpen, setDraftMenuOpen] = useState(false);
    const [moreAnchor, setMoreAnchor] = useState(null);
    const [moreOpen, setMoreOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [promoteOpen, setPromoteOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const several = mains.length > 1;
    const activeAlternate = alternates.find((d)=>d.documentId === selectedDocumentId);
    const options = activeMain ? [
        {
            value: activeMain.documentId,
            label: several ? activeMain.label || 'Main draft' : 'Main draft'
        },
        ...alternates.map((d)=>({
                value: d.documentId,
                label: d.label || 'Untitled draft'
            }))
    ] : [];
    const [actionError, setActionError] = useState(null);
    // Draft actions keep their dialog open and say why when they fail.
    const run = async (fn)=>{
        setBusy(true);
        setActionError(null);
        try {
            await fn();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'The action could not be completed.');
        } finally{
            setBusy(false);
        }
    };
    const errorText = actionError ? /*#__PURE__*/ _jsx(SpecraText, {
        role: "alert",
        size: "caption",
        tone: "danger",
        children: actionError
    }) : null;
    const filters = /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-[var(--space-2)]",
        children: [
            several ? /*#__PURE__*/ _jsx(SpecraSelect, {
                "aria-label": "Document",
                "data-testid": Testids.officeWorkspace.documentSelect,
                onChange: (e)=>onSelectDocument(e.target.value),
                size: "sm",
                value: activeMain?.documentId ?? '',
                children: mains.map((m)=>/*#__PURE__*/ _jsx("option", {
                        value: m.documentId,
                        children: m.label || 'Document'
                    }, m.documentId))
            }) : null,
            options.length > 0 ? /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "Drafts",
                onValueChange: onSelectDocument,
                options: options,
                size: "sm",
                testId: Testids.officeWorkspace.draftControl,
                value: selectedDocumentId
            }) : null,
            canEdit && activeMain ? /*#__PURE__*/ _jsx(NewDraftPopover, {
                disabled: busy,
                onCreate: onCreateDraft,
                recentVersions: recentVersions
            }) : null,
            canEdit && activeAlternate ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        "data-testid": Testids.officeWorkspace.draftMenu,
                        label: "Draft actions",
                        onClick: ()=>setDraftMenuOpen((o)=>!o),
                        ref: setDraftMenuAnchor,
                        size: "sm",
                        variant: "ghost",
                        children: /*#__PURE__*/ _jsx(MoreHorizontal, {
                            "aria-hidden": "true",
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraPopover, {
                        anchor: draftMenuAnchor,
                        "aria-label": "Draft actions",
                        onOpenChange: setDraftMenuOpen,
                        open: draftMenuOpen,
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex w-56 flex-col",
                            children: [
                                /*#__PURE__*/ _jsx(MenuItem, {
                                    onClick: ()=>{
                                        setDraftMenuOpen(false);
                                        setRenameValue(activeAlternate.label);
                                        setActionError(null);
                                        setRenameOpen(true);
                                    },
                                    testId: Testids.officeWorkspace.renameDraft,
                                    children: "Rename"
                                }),
                                /*#__PURE__*/ _jsx(MenuItem, {
                                    onClick: ()=>{
                                        setDraftMenuOpen(false);
                                        setActionError(null);
                                        setPromoteOpen(true);
                                    },
                                    testId: Testids.officeWorkspace.promoteDraft,
                                    children: "Use as main draft"
                                }),
                                /*#__PURE__*/ _jsx(MenuItem, {
                                    onClick: ()=>{
                                        setDraftMenuOpen(false);
                                        void run(()=>onArchiveDraft(activeAlternate));
                                    },
                                    testId: Testids.officeWorkspace.archiveDraft,
                                    children: "Archive"
                                })
                            ]
                        })
                    })
                ]
            }) : null
        ]
    });
    const actions = /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-[var(--space-2)]",
        children: [
            onCompare ? /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.officeWorkspace.compare,
                onClick: onCompare,
                size: "sm",
                variant: "secondary",
                children: "Compare"
            }) : null,
            canExport ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "aria-expanded": downloadOpen,
                        "data-testid": Testids.officeWorkspace.downloadMenu,
                        onClick: ()=>setDownloadOpen((o)=>!o),
                        ref: setDownloadAnchor,
                        size: "sm",
                        variant: "secondary",
                        children: "Download"
                    }),
                    /*#__PURE__*/ _jsx(SpecraPopover, {
                        align: "end",
                        anchor: downloadAnchor,
                        "aria-label": "Download",
                        onOpenChange: setDownloadOpen,
                        open: downloadOpen,
                        children: /*#__PURE__*/ _jsx(SpecraActionList, {
                            className: "w-64",
                            getItemTestId: (item)=>DOWNLOAD_TESTIDS[item.id],
                            items: downloadItems(preferredFormat, canEdit, (format, strip)=>{
                                setDownloadOpen(false);
                                onDownload(format, strip);
                            }),
                            trailing: null
                        })
                    })
                ]
            }) : null,
            canEdit && onReplaceFromFile ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        "data-testid": Testids.officeWorkspace.moreMenu,
                        label: "More",
                        onClick: ()=>setMoreOpen((o)=>!o),
                        ref: setMoreAnchor,
                        size: "sm",
                        variant: "ghost",
                        children: /*#__PURE__*/ _jsx(MoreHorizontal, {
                            "aria-hidden": "true",
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraPopover, {
                        align: "end",
                        anchor: moreAnchor,
                        "aria-label": "More document actions",
                        onOpenChange: setMoreOpen,
                        open: moreOpen,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "flex w-60 flex-col",
                            children: /*#__PURE__*/ _jsx(MenuItem, {
                                onClick: ()=>{
                                    setMoreOpen(false);
                                    onReplaceFromFile();
                                },
                                testId: Testids.officeWorkspace.replaceFromFile,
                                children: "Replace with a project file…"
                            })
                        })
                    })
                ]
            }) : null,
            cta ? cta.disabled && cta.disabledReason ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: cta.disabledReason,
                children: /*#__PURE__*/ _jsx("span", {
                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": cta.testId ?? Testids.officeWorkspace.cta,
                        disabled: true,
                        size: "sm",
                        children: cta.label
                    })
                })
            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": cta.testId ?? Testids.officeWorkspace.cta,
                disabled: cta.disabled,
                onClick: (e)=>cta.onClick(e.currentTarget),
                size: "sm",
                children: cta.label
            }) : null,
            onShowSide ? /*#__PURE__*/ _jsx(SpecraIconButton, {
                "data-testid": Testids.officeWorkspace.sideCollapse,
                label: "Show side panel",
                onClick: onShowSide,
                size: "sm",
                variant: "ghost",
                children: /*#__PURE__*/ _jsx(PanelRightOpen, {
                    "aria-hidden": "true",
                    className: "h-4 w-4"
                })
            }) : null
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                actions: actions,
                "data-testid": Testids.officeWorkspace.actionBar,
                filters: filters,
                resultSummary: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            "data-testid": Testids.officeWorkspace.saveStatus,
                            size: "meta",
                            tone: "muted",
                            children: status
                        }),
                        budget ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraTooltip, {
                                    content: budgetBasisText(budget),
                                    children: /*#__PURE__*/ _jsxs(SpecraText, {
                                        "data-testid": Testids.officeWorkspace.budget,
                                        size: "meta",
                                        tabIndex: 0,
                                        tone: overBudget(budget) ? 'danger' : 'muted',
                                        children: [
                                            "· ",
                                            budgetText(budget)
                                        ]
                                    })
                                }),
                                onCheckExactPages && budget.pagesBasis !== 'exact' ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.officeWorkspace.exactPages,
                                    disabled: checkingExactPages,
                                    onClick: onCheckExactPages,
                                    size: "inline",
                                    variant: "link",
                                    children: checkingExactPages ? 'Measuring…' : 'Check exact pages'
                                }) : null
                            ]
                        }) : null,
                        !renameOpen && !promoteOpen ? errorText : null
                    ]
                }),
                search: false
            }),
            activeAlternate ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                        footerProps: {
                            confirmLabel: 'Rename',
                            confirmDisabled: !renameValue.trim() || busy,
                            loading: busy,
                            onCancel: ()=>setRenameOpen(false),
                            onConfirm: ()=>void run(async ()=>{
                                    await onRenameDraft(activeAlternate, renameValue.trim());
                                    setRenameOpen(false);
                                })
                        },
                        onOpenChange: setRenameOpen,
                        open: renameOpen,
                        size: "sm",
                        title: "Rename draft",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col gap-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Name",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "office-rename-draft",
                                        onChange: (e)=>setRenameValue(e.target.value),
                                        value: renameValue
                                    })
                                }),
                                errorText
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                        description: `Replace the main draft with “${activeAlternate.label}”? The current main stays in History${mainVersionNumber ? ` as v${mainVersionNumber}` : ''}.`,
                        footerProps: {
                            confirmLabel: 'Use as main draft',
                            loading: busy,
                            onCancel: ()=>setPromoteOpen(false),
                            onConfirm: ()=>void run(async ()=>{
                                    await onPromoteDraft(activeAlternate);
                                    setPromoteOpen(false);
                                })
                        },
                        onOpenChange: setPromoteOpen,
                        open: promoteOpen,
                        size: "sm",
                        testId: Testids.officeWorkspace.promoteDraftDialog,
                        title: "Use as main draft",
                        children: errorText
                    })
                ]
            }) : null
        ]
    });
}
